/**
 * nodeTypeRegistry — Single source of truth for all user-addable pipeline node types.
 *
 * Used by: CommandPalette, AddNodeFAB, PipelineContextMenu, useNodeSelection,
 * NodeInspectorPanel, and any future UI that creates transform nodes.
 *
 * Does NOT include: source, source_group, mapping_output, pipeline_output,
 * destination, target_table, schema_map (agent-driven).
 */
import React from 'react';
import {
  Transform as SqlIcon,
  Code as PythonIcon,
  CompareArrows as MatchIcon,
  Psychology as LlmIcon,
  Api as ApiIcon,
  Verified as GateIcon,
} from '@mui/icons-material';

// ── Types ────────────────────────────────────────────────────────────

export interface NodeTypeDefinition {
  /** Operation type key, e.g. 'sql_transform'. */
  type: string;
  /** Full display label, e.g. 'SQL Transform'. */
  label: string;
  /** Short label for compact display, e.g. 'SQL'. */
  shortLabel: string;
  /** MUI icon component for this type. */
  icon: React.ComponentType<{ sx?: object }>;
  /** Brand color (hex). Matches NODE_COLORS in DAGNode / NodeInspectorPanel. */
  color: string;
  /** Grouping category for palette display. */
  category: 'transform' | 'enrich' | 'quality';
  /** Search keywords for command palette fuzzy match. */
  keywords: string[];
  /** True for types that incur LLM/API costs (llm_extract, llm_classify, api_enrich). */
  costImplication: boolean;
  /** One-liner description shown in palette. */
  description: string;
  /** True if this type is not yet available and should be shown as "Coming Soon". */
  comingSoon?: boolean;
}

// ── Union type for all addable node types ─────────────────────────────

export type AddNodeType =
  | 'sql_transform'
  | 'python_transform'
  | 'fuzzy_match'
  | 'llm_extract'
  | 'llm_classify'
  | 'api_enrich'
  | 'validation';

// ── Registry ─────────────────────────────────────────────────────────

export const NODE_TYPE_REGISTRY: NodeTypeDefinition[] = [
  {
    type: 'sql_transform',
    label: 'SQL Transform',
    shortLabel: 'SQL',
    icon: SqlIcon,
    color: '#0891b2',
    category: 'transform',
    keywords: ['sql', 'query', 'select', 'filter', 'join', 'aggregate', 'group', 'transform'],
    costImplication: false,
    description: 'Write SQL to transform, filter, join, or aggregate tables',
  },
  {
    type: 'python_transform',
    label: 'Python Transform',
    shortLabel: 'Python',
    icon: PythonIcon,
    color: '#7c3aed',
    category: 'transform',
    keywords: ['python', 'pandas', 'code', 'script', 'custom', 'function', 'transform'],
    costImplication: false,
    description: 'Write Python code with pandas DataFrames',
  },
  {
    type: 'fuzzy_match',
    label: 'Fuzzy Match',
    shortLabel: 'Fuzzy',
    icon: MatchIcon,
    color: '#d97706',
    category: 'enrich',
    keywords: ['fuzzy', 'match', 'deduplicate', 'dedup', 'merge', 'similar', 'approximate'],
    costImplication: false,
    description: 'Match and deduplicate records using fuzzy string comparison',
    comingSoon: true,
  },
  {
    type: 'llm_extract',
    label: 'LLM Extract',
    shortLabel: 'Extract',
    icon: LlmIcon,
    color: '#ec4899',
    category: 'enrich',
    keywords: ['llm', 'extract', 'ai', 'parse', 'unstructured', 'nlp', 'gpt', 'claude'],
    costImplication: true,
    description: 'Extract structured fields from text using an LLM',
    comingSoon: true,
  },
  {
    type: 'llm_classify',
    label: 'LLM Classify',
    shortLabel: 'Classify',
    icon: LlmIcon,
    color: '#8b5cf6',
    category: 'enrich',
    keywords: ['llm', 'classify', 'categorize', 'label', 'ai', 'sentiment', 'tag'],
    costImplication: true,
    description: 'Classify or tag records using an LLM',
    comingSoon: true,
  },
  {
    type: 'api_enrich',
    label: 'API Enrich',
    shortLabel: 'API',
    icon: ApiIcon,
    color: '#059669',
    category: 'enrich',
    keywords: ['api', 'enrich', 'lookup', 'external', 'http', 'rest', 'webhook'],
    costImplication: true,
    description: 'Enrich data by calling an external API',
    comingSoon: true,
  },
  {
    type: 'validation',
    label: 'Validation Gate',
    shortLabel: 'Validate',
    icon: GateIcon,
    color: '#ea580c',
    category: 'quality',
    keywords: ['validate', 'check', 'quality', 'assert', 'constraint', 'rule', 'gate'],
    costImplication: false,
    description: 'Add data quality checks and validation rules',
    comingSoon: true,
  },
];

// ── Lookup helpers ───────────────────────────────────────────────────

const _registryMap = new Map(NODE_TYPE_REGISTRY.map((d) => [d.type, d]));

/** Get a node type definition by type string. */
export function getNodeTypeDef(type: string): NodeTypeDefinition | undefined {
  return _registryMap.get(type);
}

/** All type strings as a Set (for fast membership checks). */
export const ADDABLE_NODE_TYPES = new Set(NODE_TYPE_REGISTRY.map((d) => d.type));

/** The top 3 types shown in the FAB (for non-power-users). */
export const FAB_NODE_TYPES: AddNodeType[] = ['sql_transform', 'python_transform'];
